import { useEffect, useId, useRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type KeyboardEvent as ReactKeyboardEvent, type ReactNode, type SelectHTMLAttributes } from 'react'
import { AlertCircle, CheckCircle2, LoaderCircle, X } from 'lucide-react'

export function Button({ className = '', variant = 'primary', ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'quiet' | 'danger' }) {
  return <button className={`button button-${variant} ${className}`} {...props} />
}

export function IconButton({ label, children, className = '', ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { label: string; children: ReactNode }) {
  return <button type="button" className={`icon-button ${className}`} aria-label={label} title={label} {...props}>{children}</button>
}

export function Field({ label, help, error, children, className = '' }: { label: string; help?: string; error?: string; children: ReactNode; className?: string }) {
  return (
    <label className={`field ${className}`}>
      <span className="field-label">{label}</span>
      {children}
      {help && <span className="field-help">{help}</span>}
      {error && <span className="field-error">{error}</span>}
    </label>
  )
}

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input className="input" {...props} />
}

export function SelectInput(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className="input select" {...props} />
}

export function EmptyState({ icon, title, children, action }: { icon?: ReactNode; title: string; children: ReactNode; action?: ReactNode }) {
  return <div className="empty-state">{icon}<h3>{title}</h3><p>{children}</p>{action}</div>
}

export function Modal({ title, children, onClose, footer }: { title: string; children: ReactNode; onClose: () => void; footer?: ReactNode }) {
  const dialog = useRef<HTMLElement>(null)
  const titleId = useId()

  useEffect(() => {
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const element = dialog.current
    if (!element) return
    const focusable = modalFocusable(element)
    ;(element.querySelector<HTMLElement>('[data-modal-initial-focus]') ?? focusable[0] ?? element).focus()
    return () => previouslyFocused?.focus()
  }, [])

  function onKeyDown(event: ReactKeyboardEvent<HTMLElement>) {
    if (event.key === 'Escape') {
      event.preventDefault()
      onClose()
      return
    }
    if (event.key !== 'Tab' || !dialog.current) return
    const focusable = modalFocusable(dialog.current)
    if (!focusable.length) {
      event.preventDefault()
      dialog.current.focus()
      return
    }
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.currentTarget === event.target) onClose() }}>
      <section ref={dialog} className="modal" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} onKeyDown={onKeyDown}>
        <header><h2 id={titleId}>{title}</h2><IconButton label="Close" onClick={onClose}><X size={18} /></IconButton></header>
        <div className="modal-body">{children}</div>
        {footer && <footer>{footer}</footer>}
      </section>
    </div>
  )
}

function modalFocusable(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
  )].filter((element) => !element.hidden && element.getAttribute('aria-hidden') !== 'true')
}

export function Notice({ kind, children, onDismiss }: { kind: 'success' | 'error' | 'info'; children: ReactNode; onDismiss?: () => void }) {
  return (
    <div className={`notice notice-${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
      {kind === 'success' ? <CheckCircle2 size={18} /> : kind === 'error' ? <AlertCircle size={18} /> : <span className="notice-dot" />}
      <span>{children}</span>
      {onDismiss && <IconButton label="Dismiss" onClick={onDismiss}><X size={16} /></IconButton>}
    </div>
  )
}

export function Loading({ label = 'Loading' }: { label?: string }) {
  return <div className="loading" role="status"><LoaderCircle className="spin" size={22} /><span>{label}</span></div>
}

export function Pill({ tone = 'neutral', children }: { tone?: 'neutral' | 'yellow' | 'green' | 'red' | 'blue'; children: ReactNode }) {
  return <span className={`pill pill-${tone}`}>{children}</span>
}

export function Segmented<T extends string>({ value, options, onChange, label, disabled = false }: { value: T; options: Array<{ value: T; label: string }>; onChange: (value: T) => void; label: string; disabled?: boolean }) {
  return <div className="segmented" role="group" aria-label={label}>{options.map((option) => <button key={option.value} type="button" className={value === option.value ? 'active' : ''} aria-pressed={value === option.value} disabled={disabled} onClick={() => onChange(option.value)}>{option.label}</button>)}</div>
}

// eslint-disable-next-line react-refresh/only-export-components
export function formatTime(minute: number) {
  const hours = Math.floor(minute / 60) % 24
  const minutes = minute % 60
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
}

// eslint-disable-next-line react-refresh/only-export-components
export function splitList(value: string) {
  return [...new Set(value.split(',').map((item) => item.trim()).filter(Boolean))]
}

// eslint-disable-next-line react-refresh/only-export-components
export function prettyLabel(value: string) {
  return value.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ').replace(/^./, (letter) => letter.toUpperCase())
}
